import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import type { AccessTokenOut, TokenPair } from './types';

const STORAGE_KEYS = {
  refresh: 'auth.refresh_token',
  email: 'auth.email',
  portalApp: 'auth.portal_app',
  accessTokens: 'auth.access_tokens',
} as const;

type AccessMatrix = Record<string, string>;

function readMatrix(): AccessMatrix {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.accessTokens);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as AccessMatrix) : {};
  } catch {
    return {};
  }
}

function writeMatrix(matrix: AccessMatrix) {
  localStorage.setItem(STORAGE_KEYS.accessTokens, JSON.stringify(matrix));
}

export const tokenStorage = {
  get refresh() {
    return localStorage.getItem(STORAGE_KEYS.refresh);
  },
  get email() {
    return localStorage.getItem(STORAGE_KEYS.email);
  },
  get portalAppName() {
    return localStorage.getItem(STORAGE_KEYS.portalApp) ?? env.portalAppName;
  },
  /** Portal access (для auth-service API). */
  get access() {
    return this.getAccess(this.portalAppName);
  },
  getAccess(appName: string): string | null {
    return readMatrix()[appName] ?? null;
  },
  setAccess(appName: string, accessToken: string) {
    const matrix = readMatrix();
    matrix[appName] = accessToken;
    writeMatrix(matrix);
  },
  invalidateAccess(appName: string) {
    const matrix = readMatrix();
    if (appName in matrix) {
      delete matrix[appName];
      writeMatrix(matrix);
    }
  },
  /** После login / refresh portal: сохранить refresh + portal access. */
  setPortalSession(pair: TokenPair, email: string, portalAppName: string = env.portalAppName) {
    localStorage.setItem(STORAGE_KEYS.refresh, pair.refresh_token);
    localStorage.setItem(STORAGE_KEYS.email, email);
    localStorage.setItem(STORAGE_KEYS.portalApp, portalAppName);
    this.setAccess(portalAppName, pair.access_token);
  },
  clear() {
    localStorage.removeItem(STORAGE_KEYS.refresh);
    localStorage.removeItem(STORAGE_KEYS.email);
    localStorage.removeItem(STORAGE_KEYS.portalApp);
    localStorage.removeItem(STORAGE_KEYS.accessTokens);
  },
};

export const api = axios.create({
  baseURL: env.authApiUrl,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.access;
  if (token && !config.headers?.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;
let exchangePromises: Record<string, Promise<string>> = {};

async function refreshPortalSession(): Promise<string> {
  const refresh = tokenStorage.refresh;
  if (!refresh) throw new Error('no refresh token');
  const { data } = await axios.post<TokenPair>(`${env.authApiUrl}/auth/refresh`, {
    refresh_token: refresh,
  });
  const portal = tokenStorage.portalAppName;
  // refresh ротирует — сбрасываем матрицу чужих aud, оставляем новый portal access
  const email = tokenStorage.email ?? '';
  tokenStorage.clear();
  tokenStorage.setPortalSession(data, email, portal);
  return data.access_token;
}

/** Access для любого app: из матрицы или POST /auth/token. */
export async function getAccessToken(appName: string, force = false): Promise<string> {
  if (!force) {
    const cached = tokenStorage.getAccess(appName);
    if (cached) return cached;
  }

  if (!exchangePromises[appName]) {
    exchangePromises[appName] = (async () => {
      const refresh = tokenStorage.refresh;
      if (!refresh) throw new Error('no refresh token');
      const { data } = await axios.post<AccessTokenOut>(`${env.authApiUrl}/auth/token`, {
        refresh_token: refresh,
        app_name: appName,
      });
      tokenStorage.setAccess(appName, data.access_token);
      return data.access_token;
    })().finally(() => {
      delete exchangePromises[appName];
    });
  }
  return exchangePromises[appName];
}

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;
    const url = original?.url ?? '';

    if (
      status === 401 &&
      !original?._retry &&
      !url.includes('/auth/login') &&
      !url.includes('/auth/refresh') &&
      !url.includes('/auth/token')
    ) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshPortalSession().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      } catch (e) {
        tokenStorage.clear();
        if (location.pathname !== '/login') location.href = '/login';
        throw e;
      }
    }
    return Promise.reject(error);
  },
);

export function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d: { loc?: unknown[]; msg?: string }) => `${(d.loc ?? []).join('.')}: ${d.msg}`).join('; ');
    }
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}
